import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Table } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import TopHeader from "components/TopHeader/topHeader";
import { useCookies } from "react-cookie";
import PaginationComponent from "components/Pagination/Pagination";
import useApi from "../../../apiHooks/apiHooks";
import TableShop from "components/Table/TableShop";
import NoData from "components/Others/NoData";
import Swal from "sweetalert2";
import displayToast from "functions/displayToast";
import flatpickr from "flatpickr";

require("flatpickr/dist/themes/airbnb.css");

export default function Shops() {
    const [cookies, setCookie] = useCookies();
    const location = useLocation();
    const userRole = location.pathname.split("/")[1];
    const header = ["Location", "Company", "POS Type", "Schedule", "Enabled"];
    const { getAllShops, editStatus, posScript } = useApi();

    // Pagination
    const itemLimit = 10;
    const [dataShown, setDataShown] = useState(null);
    const [dataLength, setDataLength] = useState(0);
    const [numOfPages, setNumOfPages] = useState(1);
    const [initialCount, setInitialCount] = useState(0);
    const [page, setPage] = useState(1);

    let flatpickrInstance;

    const handlePageChange = (newPage) => {
        const from = newPage * itemLimit;
        const until = from + itemLimit;
        setDataShown(data.slice(from, until));
        setPage(newPage + 1);
        setInitialCount(newPage * itemLimit);
    };

    const handlePrevious = () => {
        const from = initialCount - itemLimit;
        const until = from + itemLimit;
        if (page > 1) {
            setDataShown(data.slice(from, until));
            setPage(page - 1);
            setInitialCount(initialCount - itemLimit);
        }
    };

    const handleNext = () => {
        const from = initialCount + itemLimit;
        const until = from + itemLimit;
        if (page < numOfPages) {
            setDataShown(data.slice(from, until));
            setPage(page + 1);
            setInitialCount(initialCount + itemLimit);
        }
    };

    // Get All Shops
    const [data, setData] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const getShops = async (archived = showArchived) => {
        let headers = {
            headers: {
                "auth-token": cookies.userToken,
            },
        };
        try {
            const response = await getAllShops(headers, 0, 0, archived);
            const shops = response.data.shops;
            setData(shops);
            setDataLength(shops.length);
            setDataShown(shops.slice(0, itemLimit));
            setSearchedData(shops.slice(0, itemLimit));
            setPage(1);
            setInitialCount(0);
        } catch (err) {
            const error = err.response;
            console.log(`err`, error);
            setData([]);
            setDataLength(0);
            setDataShown([]);
            setSearchedData([]);
        }
    };

    useEffect(() => {
        getShops(showArchived);
    }, [showArchived]);

    useEffect(() => {
        setNumOfPages(Math.max(1, Math.ceil(dataLength / itemLimit)));
    }, [dataLength]);

    // Change Enabled Status
    const changeEnabled = async (id, status) => {
        let headers = {
            headers: {
                "auth-token": cookies.userToken,
            },
        };
        let body = {
            is_enabled: status,
        };
        try {
            await editStatus(id, body, headers);
            let newDataShown = [...dataShown];
            newDataShown[
                newDataShown.findIndex((value) => {
                    return value._id == id;
                })
            ].is_enabled = status;
            setDataShown(newDataShown);
            displayToast("success", "Status changed successfully");
            //window.location.reload();
        } catch (err) {
            const error = err.response;
            console.log(`err`, error);
            displayToast("error", "An Error has occurred");
        }
    };

    const archiveShop = async (id) => {
        const result = await Swal.fire({
            title: "Archive Shop",
            text: "Do you wish to archive this shop?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Archive",
        });

        if (!result.isConfirmed) return;

        let headers = {
            headers: {
                "auth-token": cookies.userToken,
            },
        };
        try {
            await editStatus(id, { archived: true, is_enabled: false }, headers);
            displayToast("success", "Shop archived successfully");
            getShops(showArchived);
        } catch (err) {
            const error = err.response;
            console.log(`err`, error);
            displayToast("error", "An Error has occurred");
        }
    };

    const unarchiveShop = async (id) => {
        const result = await Swal.fire({
            title: "Unarchive Shop",
            text: "Do you wish to unarchive this shop? Scheduled runs will remain disabled until you enable them.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Unarchive",
        });

        if (!result.isConfirmed) return;

        let headers = {
            headers: {
                "auth-token": cookies.userToken,
            },
        };
        try {
            await editStatus(id, { archived: false }, headers);
            displayToast("success", "Shop unarchived successfully");
            getShops(showArchived);
        } catch (err) {
            const error = err.response;
            console.log(`err`, error);
            displayToast("error", "An Error has occurred");
        }
    };

    // Search Bar
    const [searchedData, setSearchedData] = useState(null);
    useEffect(() => {
        setSearchedData(dataShown);
    }, [dataShown]);

    const onSearch = (event) => {
        let keyword = event.target.value.toLowerCase();
        setSearchedData(
            data.filter((row) => {
                return (
                    row.Company?.name?.toLowerCase().includes(keyword) ||
                    row.location?.toLowerCase().includes(keyword) ||
                    row.pos_type?.toLowerCase().includes(keyword)
                );
            })
        );
    };

    // Page Number Button Component
    let pageNumbers = [];
    for (var i = 0; i < numOfPages; i++) {
        pageNumbers.push(i);
    }

    const runPosScript = async (id) => {
        Swal.fire({
            title: "Running POS Script",
            didOpen: () => {
                Swal.showLoading();
            },
        });
        let headers = {
            headers: {
                "auth-token": cookies.userToken,
            },
        };
        try {
            const response = await posScript(id, headers);
            Swal.close();
            const failedDates = response.data?.failed_dates || [];
            if (failedDates.length > 0) {
                displayToast(
                    "error",
                    `POS Script failed: ${failedDates
                        .map((item) => item.date)
                        .join(", ")}`
                );
                return;
            }
            displayToast("success", "POS Script ran successfully");

            //window.location.reload();
        } catch (err) {
            Swal.close();
            const failedDates = err.response?.data?.failed_dates || [];
            const message =
                failedDates[0]?.message ||
                err.response?.data?.message ||
                "An Error has occurred";
            console.log(`err`, err.response);
            displayToast("error", message);
        }
    };

    const runDateScript = async (id) => {
        const getDateCount = (startDate, endDate) => {
            const start = new Date(startDate);
            const end = new Date(endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
            return Math.round((end - start) / 86400000) + 1;
        };

        const result = await Swal.fire({
            title: "Please select date range to run the Script",
            html: '<input class="swal2-input" id="run-date-range" placeholder="From date to date">',
            // stopKeydownPropagation: false,
            showCancelButton: true,
            preConfirm: () => {
                if (flatpickrInstance.selectedDates.length < 1) {
                    Swal.showValidationMessage(
                        `Please select a start date`
                    );
                } else {
                    const startDate = flatpickrInstance.selectedDates[0];
                    const endDate =
                        flatpickrInstance.selectedDates[1] || startDate;
                    return {
                        startDate,
                        endDate,
                    };
                }
            },
            willOpen: () => {
                flatpickrInstance = flatpickr(
                    Swal.getPopup().querySelector("#run-date-range"),
                    {
                        mode: "range",
                    }
                );
            },
        });

        if (result.isConfirmed) {
            let range = result.value;
            if (range?.startDate && range?.endDate) {
                Swal.fire({
                    title: "Running POS Script",
                    didOpen: () => {
                        Swal.showLoading();
                    },
                });
                let headers = {
                    headers: {
                        "auth-token": cookies.userToken,
                    },
                };
                try {
                    const response = await posScript(
                        id,
                        headers,
                        range.startDate,
                        range.endDate
                    );
                    const fallbackDateCount = getDateCount(
                        range.startDate,
                        range.endDate
                    );
                    const logsCreated =
                        response.data?.logs_created ||
                        response.data?.dates?.length ||
                        fallbackDateCount;
                    const failedDates = response.data?.failed_dates || [];
                    const dateText = logsCreated === 1 ? "date" : "dates";
                    Swal.close();
                    if (failedDates.length > 0) {
                        displayToast(
                            "warning",
                            `POS Script completed for ${logsCreated} ${dateText}; failed: ${failedDates
                                .map((item) => item.date)
                                .join(", ")}`
                        );
                    } else {
                        displayToast(
                            "success",
                            `POS Script ran successfully for ${logsCreated} ${dateText}`
                        );
                    }

                    //window.location.reload();
                } catch (err) {
                    Swal.close();
                    const error = err.response;
                    console.log(`err`, error);
                    displayToast("error", "An Error has occurred");
                }
            }
        }
    };

    return (
        <div className="m-5">
            <TopHeader title="Shops" searchBar onChange={onSearch} />
            <Card className="my-5">
                <CardBody>
                    {userRole === "admin" && (
                        <div
                            style={{
                                display: "flex",
                                gap: "1rem",
                                alignItems: "center",
                                marginBottom: "2rem",
                            }}
                        >
                            <Link to="/admin/shops/create">
                                <Button
                                    style={{
                                        backgroundColor: "#01497C",
                                        color: "white",
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                    type="button"
                                >
                                    <span className="btn-inner--icon">
                                        <i
                                            className="ni ni-fat-add"
                                            style={{ fontSize: 20 }}
                                        />
                                    </span>
                                    <span className="btn-inner--text">
                                        Create New
                                    </span>
                                </Button>
                            </Link>
                            <Button
                                color="secondary"
                                outline={!showArchived}
                                type="button"
                                onClick={() => setShowArchived(!showArchived)}
                            >
                                {showArchived ? "View Active" : "View Archived"}
                            </Button>
                        </div>
                    )}

                    <TableShop
                        header={header}
                        userRole={userRole}
                        searchedData={searchedData}
                        changeEnabled={changeEnabled}
                        runPosScript={runPosScript}
                        runDateScript={runDateScript}
                        archiveShop={archiveShop}
                        unarchiveShop={unarchiveShop}
                        showArchived={showArchived}
                    />

                    {searchedData?.length < 1 && (
                        <NoData
                            text={
                                showArchived
                                    ? "No Archived Shops Found"
                                    : "No Shops Found"
                            }
                        />
                    )}

                    <div className="mt-5">
                        <PaginationComponent
                            page={page}
                            handlePrevious={handlePrevious}
                            handleNext={handleNext}
                            handlePageChange={handlePageChange}
                            numOfPages={numOfPages}
                            pageNumbers={pageNumbers}
                        />
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}
